import React, { useState, useRef, useEffect, useImperativeHandle, useCallback } from "react";
import Input from "../../elements/input";
import { SuperButton } from "../../elements";
import { S_LANG, webStorage } from "../../utils/webStorage";
import api from "../../utils/api";
import { isBizNull } from "../../utils";
import { connect, useSelector } from "react-redux";
import { selectUserState, staffInfoSelector } from "../../selectors/application";
import { getMarketCountry, isLoggedIn } from "../../services/siteService";
import { addressTypes } from "../../utils/lynkco-constants";
import InputRow from "../../elements/input-row";

const PdpShippingInfoForm = React.forwardRef((props, ref) => {
  /*Redux start*/
  const user = useSelector(selectUserState);
  const staffInfo = useSelector(staffInfoSelector);
  /*Redux end*/
  const { fields, isDisabled, address, savable = true, onSaveSuccess, onPostalCodeChange, onCityChange } = props;
  const {
    streetLabel,
    streetMissingMessage,
    apartmentLabel,
    postalCodeLabel,
    postalCodeMissingMessage,
    postalCodeWarningMessage,
    cityLabel,
    cityMissingMessage,
    // addressMissingMessage,
    // addressUnsavedMessage,
    ctaName,
    ctaType,
    colorScheme,
    iconName
  } = fields;
  const lang = webStorage.get(S_LANG);

  const [street, setStreet] = useState(address?.street);
  const [streetError, setStreetError] = useState(null);
  const [apartment, setApartment] = useState(address?.apartment);
  const [apartmentError, setApartmentError] = useState(null);
  const [postalCode, setPostalCode] = useState(address?.postalCode);
  const [postalCodeError, setPostalCodeError] = useState(null);
  const [postalCodeIsValid, setPostalCodeIsValid] = useState(true);
  const [city, setCity] = useState(address?.city);
  const [cityError, setCityError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isInStore = useCallback(() => !!staffInfo, [staffInfo]);
  const rootEl = useRef();
  useEffect(() => {
    setStreet(address?.street);
    setApartment(address?.apartment);
    setPostalCode(address?.postalCode);
    setCity(address?.city);
  }, [address]);
  useImperativeHandle(ref, () => ({
    setError(error) {
      setFormError(error);
    },
    validateForm() {
      return {
        isValid: validateFields(),
        formData: {
          street,
          apartment,
          postalCode,
          city,
          unSave: true
        }
      };
    },
    getEl() {
      return rootEl.current;
    }
  }));
  const validateFields = () => {
    let valid = true;

    if (isBizNull(street)) {
      setStreetError(streetMissingMessage.value);
      valid = false;
    }
    if (isBizNull(postalCode)) {
      setPostalCodeError(postalCodeMissingMessage.value);
      valid = false;
    } else if (!postalCodeIsValid) {
      setPostalCodeError(postalCodeWarningMessage.value);
      valid = false;
    }
    if (isBizNull(city)) {
      setCityError(cityMissingMessage.value);
      valid = false;
    }
    return valid;
  };

  const onAutocompleteLocation = address => {
    if (address.street) setStreet(address.street);
    setCity(address.city ? address.city : "");
    setPostalCode(address.postalCode ? address.postalCode : "");
  };

  const streetProps = {
    label: streetLabel.value,
    value: street,
    showError: streetError !== null,
    warningMessage: streetError,
    disabled: isDisabled,
    location: { onAutocomplete: onAutocompleteLocation },
    onFocus: evt => {
      setStreetError(null);
    },
    onBlur: evt => {
      setStreet(evt.target.value);
    }
  };
  const apartmentProps = {
    label: apartmentLabel.value,
    value: apartment,
    showError: apartmentError !== null,
    warningMessage: apartmentError,
    disabled: isDisabled,
    onFocus: evt => {
      setApartmentError(null);
    },
    onBlur: evt => {
      setApartment(evt.target.value);
    }
  };
  const postalCodeProps = {
    label: postalCodeLabel.value,
    value: postalCode,
    showError: postalCodeError !== null,
    warningMessage: postalCodeError,
    disabled: isDisabled,
    onFocus: evt => {
      setPostalCodeError(null);
    },
    onBlur: (evt, isValid) => {
      setPostalCode(evt.target.value);
      setPostalCodeIsValid(isValid);
      onPostalCodeChange && onPostalCodeChange(evt, evt.target.value);
    },
    className: "right-gap"
  };
  const cityProps = {
    label: cityLabel.value,
    value: city,
    showError: cityError !== null,
    warningMessage: cityError,
    disabled: isDisabled,
    onFocus: evt => {
      setCityError(null);
    },
    onBlur: evt => {
      setCity(evt.target.value);
      onCityChange && onCityChange(evt, evt.target.value);
    }
  };
  const btnProps = ctaName && {
    buttonText: ctaName.value,
    ctaType,
    colorScheme,
    iconName,
    isDisabled: submitting,
    callback: async () => {
      setFormError(null);
      if (validateFields()) {
        setSubmitting(true);
        const country = await getMarketCountry();
        // console.log("country", country);
        // console.log("address", address);
        let action = "create";
        const body = {
          firstName: user?.firstName,
          lastName: user?.lastName,
          street: street,
          apartment: apartment,
          postalCode: postalCode,
          city: city,
          countryCode: country.regionAbbr,
          countryId: country.regionId + "",
          country: country.regionName,
          defaultFlag: "0",
          addressType: addressTypes.shipping
        };
        if (address?.addressId) {
          action = "update";
          body.addressId = address.addressId;
          body.defaultFlag = address.defaultFlag;
        }
        await api.address[action](null, body);
        if (onSaveSuccess) {
          if (address?.addressId) {
            Object.assign(address, {
              ...body
            });
            onSaveSuccess(address);
          } else {
            onSaveSuccess({
              ...body
            });
          }
        }
      }
    }
  };
  return (
    <form className="shipping-info-form" ref={rootEl}>
      <Input name="address-line1" autocomplete="address-line1" {...streetProps} />
      <Input name="address-line2" autocomplete="address-line2" {...apartmentProps} />
      <InputRow>
        <Input name="postal-code" autocomplete="postal-code" type={/^nl-nl$/i.test(lang) ? "postalCode" : null} {...postalCodeProps} {...{ className: "postalCode" }} />
        <Input {...cityProps} name="address-level2" autocomplete="address-level2" />
      </InputRow>
      {formError && <div className="failed-message">{formError}</div>}
      {isLoggedIn() && savable && <SuperButton {...btnProps} />}
    </form>
  );
});
PdpShippingInfoForm.displayName = "ShippingInfoForm";
export default PdpShippingInfoForm;
